import React from 'react';
import { Container, Network, GitBranch, Layers, Server, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionShell, SectionInner, SectionHeader } from '@/components/section/sectionLayout';

const DevOpsShowcase = () => {
  const pipeline = [
    { icon: GitBranch, label: 'Git Push', color: 'text-teal-400' },
    { icon: Check, label: 'Tests', color: 'text-teal-400' },
    { icon: Container, label: 'Build', color: 'text-teal-400' },
    { icon: Layers, label: 'Deploy', color: 'text-teal-400' },
    { icon: Server, label: 'Production', color: 'text-teal-400' },
  ];

  const architecture = [
    {
      title: 'Containerisation',
      description: 'Docker pour environnements reproductibles',
      icon: Container,
      items: ['Dockerfiles optimisés', 'Multi-stage builds', 'Docker Compose'],
    },
    {
      title: 'CI/CD',
      description: 'Pipelines automatisés GitHub Actions',
      icon: GitBranch,
      items: ['Tests automatisés', 'Déploiement continu', 'Rollback automatique'],
    },
    {
      title: 'Infrastructure',
      description: 'Architecture cloud scalable',
      icon: Network,
      items: ['Kubernetes orchestration', 'Load balancing', 'Auto-scaling'],
    },
    {
      title: 'Monitoring',
      description: 'Surveillance et alertes en temps réel',
      icon: Server,
      items: ['Prometheus metrics', 'Grafana dashboards', 'Log aggregation'],
    },
  ];

  return (
    <SectionShell id="devops" variant="muted">
      <SectionInner>
        <SectionHeader
          title="Architecture &"
          highlight="DevOps"
          description="De la conception à la production : une approche DevOps complète"
        />

        <div className="mb-10 sm:mb-16">
          <h3 className="mb-6 text-center text-xl font-bold text-gray-900 dark:text-white sm:mb-8 sm:text-2xl">
            Pipeline CI/CD
          </h3>
          <Card className="border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e]">
            <CardContent className="p-4 sm:p-6 md:p-8">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between lg:gap-2">
                {pipeline.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <React.Fragment key={step.label}>
                      <div className="flex flex-row items-center gap-4 sm:flex-col sm:gap-3">
                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-emerald-500/30 bg-gray-100 dark:bg-[#0f1419] sm:h-16 sm:w-16">
                          <Icon className={`h-7 w-7 sm:h-8 sm:w-8 ${step.color}`} />
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 sm:text-center">
                          {step.label}
                        </span>
                      </div>
                      {index < pipeline.length - 1 && (
                        <>
                          <div className="ml-7 h-6 w-0.5 bg-gradient-to-b from-emerald-500 to-teal-500 lg:hidden" />
                          <div className="mx-1 hidden h-0.5 flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 lg:block" />
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2">
          {architecture.map((item) => {
            const Icon = item.icon;
            return (
              <Card
                key={item.title}
                className="border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e]"
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 sm:h-12 sm:w-12">
                      <Icon className="h-5 w-5 text-emerald-400 sm:h-6 sm:w-6" />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-lg text-gray-900 dark:text-white sm:text-xl">
                        {item.title}
                      </CardTitle>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {item.items.map((subItem) => (
                      <li
                        key={subItem}
                        className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
                      >
                        <span className="shrink-0 text-emerald-400">✓</span>
                        <span>{subItem}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-8 sm:mt-12">
          <Card className="border-gray-200 bg-white dark:border-[#1f2937] dark:bg-[#1a1f2e]">
            <CardHeader>
              <CardTitle className="text-center text-lg text-gray-900 dark:text-white sm:text-xl">
                Stack DevOps
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4">
                {['Docker', 'GitHub Actions', 'Apache', 'Nginx', 'Redis', 'PostgreSQL'].map((tech) => (
                  <div
                    key={tech}
                    className="rounded-lg border border-emerald-500/30 bg-gray-100 px-3 py-2 text-xs font-medium text-emerald-600 dark:bg-[#0f1419] dark:text-emerald-400 sm:px-5 sm:py-3 sm:text-sm"
                  >
                    {tech}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </SectionInner>
    </SectionShell>
  );
};

export default DevOpsShowcase;
